import { TarWriter } from '@gera2ld/tarjs'
import { FaFolder } from '@react-icons/all-files/fa/FaFolder'
import { GoTrash } from '@react-icons/all-files/go/GoTrash'
import dayjs from 'dayjs'
import ignore from 'ignore'
import pako from 'pako'
import React, { useCallback, useEffect, useState } from 'react'
import { FileUploadIcon } from '~/constants/icon'
import { uploadFile } from '~/services/campaignService'
import { isTar } from '~/utils/blob-types'
import { getDeviceId } from '~/utils/storage'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'
import GitIgnore from './.gitignore'

interface MuleProjectUploadProps {
  onProgress?: (progress: number) => void
  onComplete?: (uploadedPath: string) => void
  className?: string
  withExamplePrefilled?: boolean
}

interface WebkitDirectoryHTMLInputElement extends HTMLInputElement {
  webkitdirectory: boolean
}

export async function loadExampleProjectAsFile(): Promise<{
  fileName: string
  file: File
}> {
  const response = await fetch('/assets/example-repository.tar.gz', {
    headers: {
      'Content-Type': 'application/gzip',
      'Content-Encoding': 'br'
    }
  })
  if (!response.ok) {
    throw new Error('Failed to fetch example project')
  }

  const blob = await response.blob()
  const blobArray = new Uint8Array(await blob.arrayBuffer())

  let gzippedTar: Uint8Array = blobArray
  if (isTar(blobArray)) {
    gzippedTar = pako.gzip(blobArray)
  }

  const clientId = getDeviceId()
  const date = dayjs().format('YYYYMMDDHHmmss')
  const fileName = 'example-mule-project.tar.gz'
  const newFileName = `${clientId}-${date}___${fileName}`

  return {
    fileName,
    file: new File([gzippedTar], newFileName, { type: 'application/gzip' })
  }
}

export async function loadExampleProjectAsS3Path(): Promise<string> {
  const { file } = await loadExampleProjectAsFile()
  return new Promise((resolve, reject) => {
    uploadFile(file, {
      onProgress: () => {},
      onComplete: uploadedPath => {
        resolve(uploadedPath)
      },
      onError: error => {
        BetterToast.error('Failed to load example project')
        reject(error)
      }
    })
  })
}

export default function MuleProjectUpload({
  onProgress = () => {},
  onComplete = () => {},
  className,
  withExamplePrefilled = false
}: MuleProjectUploadProps) {
  const [dragOver, setDragOver] = useState(false)
  const [projectName, setProjectName] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<string | null>(null)

  useEffect(() => {
    if (withExamplePrefilled) {
      loadExampleProject()
    }
  }, [withExamplePrefilled])

  const loadExampleProject = async () => {
    try {
      setUploadStatus('Loading example project...')
      const { fileName, file } = await loadExampleProjectAsFile()
      setProjectName(fileName)
      onTarFileChange(file)
    } catch (error) {
      console.error('Error loading example project:', error)
      BetterToast.error('Failed to load example project')
      setUploadStatus(null)
    }
  }

  const handleClear = useCallback(() => {
    if (withExamplePrefilled) return
    setProjectName(null)
    setUploadStatus(null)
    onComplete('')
  }, [onComplete, withExamplePrefilled])

  const onTarFileChange = useCallback(
    (tarFile: File | null) => {
      if (tarFile) {
        if (withExamplePrefilled) {
          setUploadStatus('Loading example project...')
        } else {
          setUploadStatus('Analyzing the project (0%)')
        }
        uploadFile(tarFile, {
          onProgress: progress => {
            if (withExamplePrefilled) {
              setUploadStatus('Loading example project...')
            } else {
              setUploadStatus(`Analyzing the project (${progress.toFixed(0)}%)`)
            }
            onProgress(progress)
          },
          onComplete: uploadedPath => {
            setUploadStatus(null)
            setProjectName(tarFile.name.replace('.tar.gz', '').split('___').pop()!)
            onComplete(uploadedPath)
          },
          onError: error => {
            BetterToast.error(`Failed to upload. ${error}`)
            handleClear()
          }
        })
      }
    },
    [onComplete, onProgress, handleClear]
  )

  const createCompressedTarArchive = useCallback(
    async (files: FileList, isDropped: boolean = false) => {
      if (withExamplePrefilled) return

      setProcessing(true)
      try {
        const tarWriter = new TarWriter()
        const fileArray = Array.from(files)
        const rootFolder = fileArray[0].webkitRelativePath.split('/')[0]

        const ig = ignore().add(GitIgnore)

        for (const file of fileArray) {
          const fileContent = await file.arrayBuffer()
          const filePath = isDropped
            ? file.webkitRelativePath.split('/').slice(1).join('/')
            : file.webkitRelativePath

          const relativePath = filePath.replace(`${rootFolder}/`, '')
          if (!ig.ignores(relativePath)) {
            await tarWriter.addFile(filePath, new Uint8Array(fileContent))
          }
        }

        const tarBlob = await tarWriter.write()
        const tarArray = new Uint8Array(await tarBlob.arrayBuffer())
        const gzippedTar = pako.gzip(tarArray)

        const tarFileName = `${fileArray[0].webkitRelativePath.split('/')[0]}.tar.gz`
        const clientId = getDeviceId()
        const date = dayjs().format('YYYYMMDDHHmmss')
        const newTarFileName = `${clientId}-${date}___${tarFileName}`

        const newTarFile = new File([gzippedTar], newTarFileName, {
          type: 'application/gzip'
        })

        setProjectName(tarFileName)
        onTarFileChange(newTarFile)
      } catch (error) {
        console.error('Error creating compressed tar archive:', error)
        BetterToast.error('Failed to create compressed tar archive of the project')
      } finally {
        setProcessing(false)
      }
    },
    [onTarFileChange, withExamplePrefilled]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (!withExamplePrefilled) {
        setDragOver(true)
      }
    },
    [withExamplePrefilled]
  )

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (withExamplePrefilled) return

      setDragOver(false)
      const items = e.dataTransfer.items

      if (items && items.length > 0) {
        const entry = items[0].webkitGetAsEntry()
        if (entry && entry.isDirectory) {
          const fileList: File[] = []
          const rootFolderName = entry.name

          const traverseFileTree = (item: FileSystemEntry, path: string) => {
            if (item.isFile) {
              ;(item as FileSystemFileEntry).file((file: File) => {
                const newFile = new File([file], file.name, { type: file.type })
                Object.defineProperty(newFile, 'webkitRelativePath', {
                  writable: true,
                  value: `${rootFolderName}/${path}${file.name}`
                })
                fileList.push(newFile)
              })
            } else if (item.isDirectory) {
              const dirReader = (item as FileSystemDirectoryEntry).createReader()
              dirReader.readEntries(entries => {
                for (let i = 0; i < entries.length; i++) {
                  traverseFileTree(entries[i], path + item.name + '/')
                }
              })
            }
          }

          traverseFileTree(entry, '')

          setTimeout(() => {
            if (fileList.length > 0) {
              const dataTransfer = new DataTransfer()
              fileList.forEach(file => dataTransfer.items.add(file))
              createCompressedTarArchive(dataTransfer.files, true)
            }
          }, 100)
        } else {
          BetterToast.error('Please drop a folder containing your Mule project')
        }
      }
    },
    [createCompressedTarArchive, withExamplePrefilled]
  )

  const handleFolderSelect = useCallback(
    (e: React.ChangeEvent<WebkitDirectoryHTMLInputElement>) => {
      if (withExamplePrefilled) return

      const files = e.target.files
      if (files && files.length > 0) {
        createCompressedTarArchive(files)
      }
    },
    [createCompressedTarArchive, withExamplePrefilled]
  )

  if (uploadStatus || projectName) {
    return (
      <div className={cm('w-full flex items-center justify-between mb-4', className)}>
        <div
          className={cm(
            'border border-[#d4dce2] rounded-2xl flex items-center min-w-[250px] flex-1',
            'overflow-hidden text-ellipsis'
          )}
        >
          <div className='file-type h-full border-r-1 border-[#d4dce2] py-2 px-5'>
            <FaFolder size={32} color='#6f7987' />
          </div>
          <div
            className={cm(
              'ml-2 whitespace-nowrap text-sm text-default-700 max-w-[70%]',
              'overflow-hidden text-ellipsis'
            )}
          >
            {uploadStatus || projectName}
          </div>
        </div>
        {!uploadStatus && (
          <div
            className={cm(
              'button-submit bg-white text-default-600',
              'px-[10px] rounded-2xl cursor-pointer',
              'border border-[#d4dce2] select-none h-8 hover:bg-light3',
              'disabled:cursor-not-allowed',
              'w-12 h-12 ml-2 flex items-center justify-center',
              withExamplePrefilled ? 'opacity-50 cursor-not-allowed' : ''
            )}
            onClick={handleClear}
          >
            <GoTrash size={16} className='w-[16px] h-[16px]' />
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={cm(
        'upload-container my-2 h-[140px] w-full border-2 border-dashed rounded-2xl',
        dragOver ? 'border-[#6f7987] bg-[#e8effb]' : 'border-[#d4dce2]',
        'flex items-center justify-center mb-4',
        withExamplePrefilled ? 'opacity-50 cursor-not-allowed' : ''
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type='file'
        id='folderUpload'
        className='hidden'
        onChange={handleFolderSelect}
        // @ts-ignore
        webkitdirectory='true'
        multiple
        disabled={withExamplePrefilled}
      />

      <label
        htmlFor='folderUpload'
        className={cm(
          'upload-inner w-[80%] flex flex-col items-center justify-center',
          withExamplePrefilled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <FileUploadIcon />
        <div className='description text-sm text-default-600 mt-2'>
          {processing ? (
            'Analyzing and compressing project...'
          ) : (
            <div className='flex justify-center flex-col items-center'>
              <div>
                {withExamplePrefilled ? (
                  'Loading example project...'
                ) : (
                  <>
                    Drag and drop or <span className='text-link'>select</span> your Mule project
                    folder
                  </>
                )}
              </div>
              <div>
                The code is used only for the purpose of the task and will be deleted once task is
                done
              </div>
            </div>
          )}
        </div>
      </label>
    </div>
  )
}
